import * as angular from 'angular';
import { FeedbackSessionControllerClass } from '../feedback-session.controller';
import { FeedbackSessionContentControllerClass } from '../content/content.controller';
import { UserService } from 'go-modules/models/user/user.service';
import { EventService } from '@go-modules/services/event/event.service';
import type { GoEvent } from '@go-modules/services/event/event.service';
import { catchError, filter, take, takeUntil } from 'rxjs/operators';
import { EVENT_NAMES } from '@go-modules/services/event/event-names.constants';
import { EnvironmentVarsService } from '@go-modules/services/environment-vars/environment-vars.service';
import { ResizeSensor } from 'css-element-queries';
import { Sizes } from 'go-modules/responsive-view/responsive-view.service';
import { CommentFilterService } from '@go-modules/services/comment-filter/comment-filter.service';
import { NgxFeatureFlagService } from '@go-modules/services/feature-flag/feature-flag.service';
import { NgxGoToastService } from '@go-modules/services/go-toast/go-toast.service';
import { GoToastStatusType } from '@go-modules/enums/go-toast-status-type';
import { AIMarkersDialogComponent } from '@go-modules/components/dialogs/ai-markers-dialog/ai-markers-dialog.component';
import type { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import { GoTourService } from 'go-modules/go-tour/go-tour.service';
import dayjs from 'dayjs';
import { SelectedService } from 'go-modules/services/selected/selected.service';
import { TranscriptCacheService } from '@go-modules/services/transcript-cache/transcript-cache.service';
import { BehaviorSubject, combineLatest, EMPTY, Subject } from 'rxjs';
import { ProductTier } from '@go-modules/interfaces/licenses/product-tier';
import { clientSettings } from 'go-modules/models/common/client.settings';
import { ExpirationPolicies } from '@go-modules/enums/salesforce-license';
import { LlmTaskService } from '@go-modules/services/llm-task/llm-task.service';
import { LlmFeatures, LlmRelations, LlmTaskStatus } from '@go-modules/interfaces/llm-task';
import { CommentListService } from '@go-modules/services/comment-list/comment-list.service';
import { TimeUtilsService } from 'go-modules/time/time.service';
import { FeedbackSessionService, FeedbackTabs } from '@go-modules/services/feedback-session/feedback-session.service';
import { COMMENT_RESOURCE_TYPES } from 'go-modules/models/comment/comment.constant';

export interface FeedbackDisplayControllerScope extends ng.IScope {
	mainController: FeedbackSessionControllerClass;
	contentController: FeedbackSessionContentControllerClass;
}

export enum TooltipAndAriaLabel {
	tooltip = 'tooltip',
	ariaLabel = 'ariaLabel'
}

export const FEEDBACK_DISPLAY_AI_MARKERS_TOUR = 'feedback-display-ai-markers-tour';
export const FEEDBACK_DISPLAY_AI_MARKERS_TOUR_CONSTRAINTS = {
	isOnboarding: false,
	tourStartDate: dayjs('2024-07-01 09:00:00').toDate()
};

export const FEEDBACK_DISPLAY_MANAGE_AI_MARKERS_TOUR = 'feedback-display-manage-ai-markers-tour';
export const FEEDBACK_DISPLAY_MANAGE_AI_MARKERS_TOUR_CONSTRAINTS = {
	isOnboarding: false,
	tourStartDate: dayjs('2025-01-31 09:00:00').toDate()
};

export enum FeedbackDisplayZeroState {
	ADD_AI_MARKERS_COMMENT = 'add-ai-markers-comment',
	ADD_COMMENTS = 'add-comments',
	NO_FEEDBACK_SHOWN = 'no-feedback-shown'
}

export class FeedbackDisplayController {
	public initialFeedbackLoaded: boolean = false;
	public feedbackLoaded$ = new BehaviorSubject<boolean>(false);
	public autoPlayEnabled: boolean;
	public tooltipAndAriaLabel = TooltipAndAriaLabel;
	public scoreAriaLabel: string;
	public environmentVarsService: EnvironmentVarsService;
	public oneClickMarkerEnabled: boolean = false;
	public rerunningAiFeedback: boolean = false;
	public hasComments = false;
	public tourCanBeViewed: boolean = false;
	public showManageAiMarkerTour = false;
	public readonly componentDestroyed$$ = new Subject();
	public zeroState: FeedbackDisplayZeroState | null = null;
	public zeroStates = FeedbackDisplayZeroState;
	public showGeneratingAiFeedbackBanner = false;
	public showSyncEventCommentsFilterButton: boolean = false;
	public activeTab: FeedbackTabs;
	public tabs = FeedbackTabs;

	private eventSubscription: any;
	private eventNames: string[];
	private sensor: ResizeSensor;

	private reloadComments$ = new BehaviorSubject<boolean>(false);
	private isReplyingOrUpdatingComment$ = new BehaviorSubject<boolean>(false);

	/* @ngInject */
	constructor (
		public $scope,
		private feedbackSession,
		public feedbackSettings,
		public ngxCommentFilterService: CommentFilterService,
		private $translate: ng.translate.ITranslateService,
		private $element: ng.IRootElementService,
		private goPrintService: any,
		private userService: UserService,
		private ngxCommentListService: CommentListService,
		private eventService: EventService,
		private featureFlag: NgxFeatureFlagService,
		private $http: ng.IHttpService,
		private ngxGoToastService: NgxGoToastService,
		private goTour: GoTourService,
		private selectedService: SelectedService,
		private ngxGoModalService: GoModalService,
		private transcriptCacheService: TranscriptCacheService,
		private llmTaskService: LlmTaskService,
		private Time: TimeUtilsService,
		private ngxFeedbackSessionService: FeedbackSessionService
	) {
		this.environmentVarsService = EnvironmentVarsService.getInstance();
	}

	/**
	 * Handles controller init lifecycle hook
	 */
	public $onInit (): void {
		this.eventNames = [
			EVENT_NAMES.FEEDBACK_SESSION_FEEDBACK_LOADED,
			EVENT_NAMES.FEEDBACK_SESSION_OPEN_AI_MARKERS,
			EVENT_NAMES.FEEDBACK_SESSION_AI_COMMENT_TASKS_COMPLETED,
			EVENT_NAMES.FEEDBACK_SESSION_AI_COMMENT_TASKS_FAILED
		];
		this.eventSubscription = this.eventService.events
			.pipe(filter((ev: GoEvent) => this.eventNames.includes(ev.name)))
			.subscribe((ev: GoEvent) => {
				switch (ev.name) {
					case EVENT_NAMES.FEEDBACK_SESSION_FEEDBACK_LOADED:
						this.feedbackLoaded();
						this.checkToShowManageAiMarkersTour();
						this.showFeedbackDisplayAiMarkersTour();
						break;
					case EVENT_NAMES.FEEDBACK_SESSION_OPEN_AI_MARKERS:
						this.openAiMarkers();
						break;
					case EVENT_NAMES.FEEDBACK_SESSION_AI_COMMENT_TASKS_COMPLETED:
						this.reloadComments$.next(true);
						break;
					case EVENT_NAMES.FEEDBACK_SESSION_AI_COMMENT_TASKS_FAILED:
						this.toggleGeneratingAiFeedbackBanner(false);

						if (this.showReRunAiFeedbackButton()) {
							this.ngxGoToastService.createToast({
								type: GoToastStatusType.ERROR,
								message: 'feedback-session-ai-feedback-failed',
								actionLink: {
									title: 'common_try-again',
									action: () => this.rerunAiFeedback()
								}
							});
						} else {
							this.ngxGoToastService.createToast({
								type: GoToastStatusType.ERROR,
								message: 'feedback-session-ai-feedback-failed'
							});
						}
						break;
				}
			});

		this.$scope.$watch('mainController.activity', this.activityWatch);
		this.autoPlayEnabled = this.feedbackSettings.autoPlay;
		this.oneClickMarkerEnabled = this.feedbackSettings.oneClickMarker;

		if (this.featureFlag.isAvailable('LLM_TASKS')) {
			this.llmTaskService.getStatus(
				LlmRelations.SESSION,
				this.$scope.mainController.session.session_id,
				LlmFeatures.AI_COMMENTS
			)
				.pipe(
					takeUntil(this.componentDestroyed$$)
				)
				.subscribe((response) => {
					this.toggleGeneratingAiFeedbackBanner(
						response.status === LlmTaskStatus.QUEUED || response.status === LlmTaskStatus.PROCESSING
					);
				});

			combineLatest([this.reloadComments$, this.isReplyingOrUpdatingComment$])
				.pipe(
					takeUntil(this.componentDestroyed$$),
					filter(([reload, isReplyingOrUpdating]) => reload && !isReplyingOrUpdating)
				)
				.subscribe(() => {
					const oldNumComments = this.ngxCommentListService.currentComments.length;

					this.$scope.mainController.sessionFeedback.get().then((_result) => {
						this.toggleGeneratingAiFeedbackBanner(false);
						const currentNumComments = this.ngxCommentListService.currentComments.length;
						const newNumComments = currentNumComments - oldNumComments;

						this.eventService.broadcast(EVENT_NAMES.FEEDBACK_SESSION_AI_COMMENTS_LOADED, newNumComments);
						this.reloadComments$.next(false);
						this.ngxGoToastService.createToast({
							type: GoToastStatusType.SUCCESS,
							message: 'feedback-session-ai-feedback-success'
						});
					});
				});
		}

		combineLatest([
			this.ngxCommentListService.comments$,
			this.feedbackLoaded$
		]).pipe(
			takeUntil(this.componentDestroyed$$)
		).subscribe(([comments, _feedbackLoaded]) => {
			this.hasComments = comments.length > 0;
			this.showSyncEventCommentsFilterButton = comments.some((comment) => {
				return comment.resource?.some((res) => res.resource_type === COMMENT_RESOURCE_TYPES.SYNC_EVENT);
			});
			this.checkZeroState(comments);
		});

		const savedFeedbackSettings = localStorage.getItem('feedbackSettings');
		if (savedFeedbackSettings) {
			const feedbackSettings = JSON.parse(savedFeedbackSettings) || {};
			if ('filterOutSyncEventComments' in feedbackSettings) {
				this.ngxCommentListService.toggleFilterOutSyncEventComments(
					feedbackSettings.filterOutSyncEventComments
				);
			}
		}

		this.ngxFeedbackSessionService.activeTab$.pipe(takeUntil(this.componentDestroyed$$)).subscribe((tab) => {
			this.activeTab = tab;
			this.safeDigest();
		});
	}

	public $onDestroy (): void {
		this.eventSubscription?.unsubscribe();
		this.sensor?.detach();
		this.componentDestroyed$$.next(true);
		this.componentDestroyed$$.complete();
	}

	public $postLink () {
		this.sensor = new ResizeSensor(this.$element[0].parentElement, (measurements) => {
			for (const css of this.$element[0].classList.values()) {
				if (css.startsWith('resize-sensor-')) {
					this.$element[0].classList.remove(css);
				}
			}

			[Sizes.XXSMALL, Sizes.XSMALL].forEach((width) => {
				if (measurements.width <= width) {
					this.$element[0].classList.add(`resize-sensor-${width}`);
				}
			});

			this.eventService.broadcast(EVENT_NAMES.DOC_RESIZE);

			this.$scope.$apply();
		});
	}

	public hideFeedbackCreator = () => {
		const { activity, session, user, license, userGroup } = this.$scope.mainController;
		return session.isLimitCommentOnlySingleAttempt(activity, user) ||
			!userGroup.validateCanCreateSession(user, license).isValid;
	};

	public mayEditScore = () => {
		if (this.environmentVarsService.get(EnvironmentVarsService.VAR.READONLY)) {
			return false;
		}

		if (this.$scope.mainController.activity.isAutoScored()) {
			return false;
		}

		if (!this.$scope.mainController.activity.mayScore(this.$scope.mainController.userGroup)) {
			return false;
		}

		return true;
	};

	public canEditCommentKind = () => {
		if (this.environmentVarsService.get(EnvironmentVarsService.VAR.READONLY)) {
			return false;
		}

		return this.$scope.mainController.activity.isCommentsEnabled()
			&& (!this.feedbackSession.isRecording() || this.$scope.mainController.activity.live_session_enabled);
	};

	public removeRubric = (rubric) => {
		this.$scope.contentController.removeFeedbackItem(rubric);
	};

	public addRubric = (rubric) => {
		this.$scope.contentController.addFeedbackItem(rubric);
	};

	/**
	 * Users should not be able toggle the auto play setting
	 * for conversational activities that don't require response media.
	 */
	public shouldDisplayAutoPlayToggleCapability (): boolean {
		return this.feedbackSession.isPlaybackMode();
	}

	/**
	 * Toggle autoplay comment
	 */
	 public toggleAutoplayComments = (event) => {
		this.autoPlayEnabled = !this.autoPlayEnabled;
		this.feedbackSettings.toggleAutoPlay(this.autoPlayEnabled);
		event.preventDefault();
	};

	/**
	 * Handles auto play setting change event
	 */
	public handleAutoPlaySettingChange (): void {
		this.feedbackSettings.toggleAutoPlay(this.autoPlayEnabled);
	}

	/**
	 * Toggle one-click marker
	 */
	 public toggleOneClickMarker = (event) => {
		this.oneClickMarkerEnabled = !this.oneClickMarkerEnabled;
		this.feedbackSettings.toggleOneClickMarker(this.oneClickMarkerEnabled);
		event.preventDefault();
	};

	/**
	 * Handles one-click marker setting change event
	 */
	 public handleOneClickMarkerSettingChange (): void {
		this.feedbackSettings.toggleOneClickMarker(this.oneClickMarkerEnabled);
	}

	public seekToTimestamp = (startMS: number) => {
		if (startMS == null) return;
		if (Number.isNaN(startMS)) return;

		const time = Math.max(0, startMS);
		this.$scope.mainController.playerSync.seek(time);
	};

	/**
	 * Toggle feedback auto play
	 */
	public toggleSyncEventCommentsFilter = () => {
		this.ngxCommentListService.toggleFilterOutSyncEventComments();
	};

	public shouldFilterOutSyncEventComments = () => {
		return this.ngxCommentListService.shouldFilterOutSyncEventComments();
	};

	/**
	 * Whether the Re-run AI Feedback button should be shown
	 *
	 * @returns {boolean}
	 */
	public showReRunAiFeedbackButton = () => {
		const { activity, session } = this.$scope.mainController;

		return this.userService.currentUser.is_root_user &&
			activity.ai_prompts_enabled &&
			!session.media.isYoutube();
	};

	/**
	 * Toggle feedback auto play
	 */
	public rerunAiFeedback = () => {
		this.rerunningAiFeedback = true;
		if (this.featureFlag.isAvailable('LLM_TASKS')) {
			this.toggleGeneratingAiFeedbackBanner(true);
		}
		const { session } = this.$scope.mainController;

		this.$http.post(`${clientSettings.GoReactV2API}/sessions/${session.session_id}/generate/ai-feedback`, {})
			.then(() => {
				this.ngxGoToastService.createToast({
					type: GoToastStatusType.SUCCESS,
					message: 'feedback-session-rerun-ai-feedback-success-message'
				});

				if (this.tourCanBeViewed) {
					this.closeFeedbackDisplayAiMarkersTour();
				}
			})
			.catch(() => {
				this.ngxGoToastService.createToast({
					type: GoToastStatusType.ERROR,
					message: 'feedback-session-rerun-ai-feedback-error-message'
				});
			})
			.finally(() => this.rerunningAiFeedback = false);
	};

	/**
	 * Get label for score display
	 *
	 * @param score
	 * @returns {string}
	 */
	public getScoreLabel = (score) => {
		let label = '—';
		score = parseFloat(score);

		if (!isNaN(score)) {
			score = Math.round(score * 100) / 100 + ''; // round to 2 decimal places
			label = score;
			this.scoreAriaLabel = this.$translate.instant('feedback-session-feedback-display_points-assigned', { score });
		} else if (this.mayEditScore() && !this.$scope.mainController.session.isScoreAssigned()) {
			label = this.$translate.instant('feedback-session_controller-placeholder-pts');
			this.scoreAriaLabel = this.$translate.instant('feedback-session-feedback-display_no-points-assigned');
		}
		return label;
	};

	public getScoreTooltipAndAriaLabel = (type: string) => {
		let ariaLabel = '';
		let tooltip = '';
		const score = this.$scope.mainController.session.score;

		if (this.$scope.mainController.activity.isRubricEnabled()) {
			tooltip = this.$translate.instant('feedback-session-feedback-display_score-calculated');

			if (score === null) {
				ariaLabel = this.$translate.instant('feedback-session-feedback-display_no-rubric-no-score-aria-label');
			} else {
				ariaLabel = this.$translate.instant('feedback-session-feedback-display_no-rubric-has-score-aria-label', {
					score: Math.round(score * 100) / 100 + '',
					possible: this.$scope.mainController.activity.getTotalPoints()
				});
			}
		} else {
			if (score === null) {
				tooltip = this.$translate.instant('feedback-session-feedback-display_no-points-assigned');
				ariaLabel = this.$translate.instant('feedback-session-feedback-display_no-points-assigned');
			} else {
				const scorePossible = {
					score: Math.round(score * 100) / 100 + '',
					possible: this.$scope.mainController.activity.getTotalPoints()
				};

				tooltip = this.$translate.instant('feedback-session-feedback-display_points-possible', scorePossible);
				ariaLabel = this.$translate.instant('feedback-session-feedback-display_points-graded', scorePossible);
			}
		}

		return type === TooltipAndAriaLabel.tooltip ? tooltip : ariaLabel;
	};

	public printComments () {
		// comments virtual scroll now so not all content is rendered
		// instead create a new element to print
		const printElement = document.createElement('div');
		printElement.style.padding = '20px';
		this.ngxCommentListService.comments$.pipe(take(1)).subscribe((comments) => {
			comments.forEach((comment) => {
				const commentElement = document.createElement('div');
				// comment.first_name + comment.last_name + comment.text
				// name should be bold
				let text = '';
				if (comment.time) {
					text += this.Time.formatTime(Number(comment.time), true, true) + ' ';
				}
				text += `<b>${comment.first_name} ${comment.last_name}</b> ${dayjs(comment.created_at).format('MMM D, YYYY h:mmA')}: ${comment.text}`;
				commentElement.innerHTML = text;
				printElement.appendChild(commentElement);
			});
			this.goPrintService.print(
				printElement,
				'Printed Comments'
			);
		});
	}

	public addAiAssistant () {
		const { activity } = this.$scope.mainController;
		const useLlmDefinitions = this.featureFlag.isAvailable('LLM_TASKS');
		const previousDefinitions = useLlmDefinitions ? structuredClone(activity.llm_definitions) : [];

		return this.ngxGoModalService.open(AIMarkersDialogComponent, true, {
			data: {
				activity,
				aiPrompts: useLlmDefinitions ? [] : activity.ai_prompts,
				llmDefinitions: useLlmDefinitions ? activity.llm_definitions : [],
				aiTranscriptionLanguage: activity.ai_transcription_language
			}
		}).afterClosed().subscribe((result) => {
			if (result) {
				this.$scope.mainController.activity.ai_transcription_language = result.ai_transcription_language;

				if (useLlmDefinitions) {
					const newDefinitions = structuredClone(result.llm_definitions);

					if (this.hasNewDefinitions(previousDefinitions, newDefinitions)) {
						this.toggleGeneratingAiFeedbackBanner(true);
					}

					this.$scope.mainController.activity.llm_definitions = result.llm_definitions;
				} else {
					this.$scope.mainController.activity.ai_prompts = result.prompts;
				}

				this.$scope.mainController.activity.save();
			}
		});
	}

	public showFeedbackDisplayAiMarkersTour () {
		const { activity, session } = this.$scope.mainController;

		if(!session.media || session.media.isYoutube() || !activity.has_response_media) return;

		this.transcriptCacheService.getTranscription({
			media: session.media,
			includeAnalytics: true
		})
			.pipe(
				takeUntil(this.componentDestroyed$$),
				catchError(() => {
					return EMPTY;
				})
			)
			.subscribe(() => {
				const license = this.selectedService.getLicense();
				const useLlmDefinitions = this.featureFlag.isAvailable('LLM_TASKS');

				if (
					this.selectedService.getGroup().hasInstructorRole(true) &&
					activity.ai_prompts_enabled &&
					((useLlmDefinitions && !activity.llm_definitions.length) ||
						(!useLlmDefinitions && !activity.ai_prompts.length)) &&
					this.hasComments &&
					this.featureFlag.isAvailable('LICENSE_UPGRADE_PURCHASE') &&
					license?.salesforce_license.tier_name === ProductTier.Plus
				) {
					this.goTour.canTourBeViewed(
						FEEDBACK_DISPLAY_AI_MARKERS_TOUR,
						FEEDBACK_DISPLAY_AI_MARKERS_TOUR_CONSTRAINTS
					).then(()=> {
						this.tourCanBeViewed = true;
					}).catch(angular.noop);
				}
			});
	}

	public closeFeedbackDisplayAiMarkersTour (): void {
		this.goTour.markTourViewed(FEEDBACK_DISPLAY_AI_MARKERS_TOUR);
		this.tourCanBeViewed = false;
	}

	public checkToShowManageAiMarkersTour () {
		const { activity, session } = this.$scope.mainController;

		if(!session.media || session.media.isYoutube() || !activity.has_response_media) return;

		const license = this.selectedService.getLicense();
		const useLlmDefinitions = this.featureFlag.isAvailable('LLM_TASKS');
		if (
			this.featureFlag.isAvailable('AI_DEFAULT') &&
			this.selectedService.getGroup().hasInstructorRole(true) &&
			license?.salesforce_license.tier_name === ProductTier.Plus &&
			activity.ai_prompts_enabled &&
			((useLlmDefinitions && activity.llm_definitions.length > 0) ||
				(!useLlmDefinitions && activity.ai_prompts.length > 0)) &&
			!(activity.is_parent_sync_enabled && activity.parent_child_sync) &&
			!this.environmentVarsService.get(EnvironmentVarsService.VAR.DISABLE_ACTIVITY_EDITING)
		) {
			this.goTour.canTourBeViewed(
				FEEDBACK_DISPLAY_MANAGE_AI_MARKERS_TOUR,
				FEEDBACK_DISPLAY_MANAGE_AI_MARKERS_TOUR_CONSTRAINTS
			).then(()=> {
				this.showManageAiMarkerTour = true;
			}).catch(angular.noop);
		}
	}

	public closeManageAiMarkersTour (): void {
		this.goTour.markTourViewed(FEEDBACK_DISPLAY_MANAGE_AI_MARKERS_TOUR);
		this.showManageAiMarkerTour = false;
	}

	public openAiMarkers () {
		const { activity } = this.$scope.mainController;
		const useLlmDefinitions = this.featureFlag.isAvailable('LLM_TASKS');
		const previousDefinitions = useLlmDefinitions ? structuredClone(activity.llm_definitions) : [];

		const modalData = {
			activity,
			aiPrompts: useLlmDefinitions ? undefined : activity.ai_prompts,
			llmDefinitions: useLlmDefinitions ? activity.llm_definitions : undefined,
			aiTranscriptionLanguage: activity.ai_transcription_language
		};

		return this.ngxGoModalService.open(AIMarkersDialogComponent, true, {
			data: modalData
		}).afterClosed().subscribe((result) => {
			if (result) {
				this.$scope.mainController.activity.ai_transcription_language = result.ai_transcription_language;

				if (useLlmDefinitions) {
					const newDefinitions = structuredClone(result.llm_definitions);

					if (this.hasNewDefinitions(previousDefinitions, newDefinitions)) {
						this.toggleGeneratingAiFeedbackBanner(true);
					}

					this.$scope.mainController.activity.llm_definitions = result.llm_definitions;
				} else {
					this.$scope.mainController.activity.ai_prompts = result.prompts;
				}

				this.$scope.mainController.activity.save();

				const hasDefinitions = useLlmDefinitions
					? result.llm_definitions && result.llm_definitions.length
					: result.prompts && result.prompts.length;

				if (hasDefinitions) {
					this.rerunAiFeedback();
				}
			}
		});
	}

	public getReadOnlyText () {
		const license = this.selectedService.getLicense();
		const group = this.selectedService.getGroup();

		if (this.environmentVarsService.get(EnvironmentVarsService.VAR.READONLY)) {
			return 'session-read-only-text_lti-read-only';
		}

		if (!this.hasCourseStarted(group)) {
			return 'session-read-only-text_course-not-started';
		}

		if (this.hasCourseEnded(group)) {
			return 'session-read-only-text_course-ended';
		}

		if (
			license &&
			(
				!this.isLicenseActive(license) ||
				(
					license.salesforce_license.expiration_policy === ExpirationPolicies.RESTRICTED &&
					(!this.hasLicenseStarted(license) || this.isLicenseExpired(license))
				)
			)
		) {
			return 'session-read-only-text_invalid-license';
		}

		return null;
	}

	private hasNewDefinitions (previousDefinitions, newDefinitions): boolean {
		const prevDefIds = new Set(previousDefinitions.map((prevDef) => prevDef.id));

  		return newDefinitions.some((newDef) => !prevDefIds.has(newDef.id));
	}

	private hasCourseStarted (group): boolean {
		const currentDateUTC = dayjs().utc();
		return !currentDateUTC.isBefore(dayjs(group.start_date));
	}

	private hasCourseEnded (group): boolean {
		const currentDateUTC = dayjs().utc();
		return group.end_date && currentDateUTC.isAfter(dayjs(group.end_date));
	}

	private isLicenseActive (license): boolean {
		return license.is_active;
	}

	private hasLicenseStarted (license): boolean {
		const currentDateUTC = dayjs().utc();
		return !currentDateUTC.isBefore(dayjs.utc(license.current_access_date));
	}

	private isLicenseExpired (license): boolean {
		const currentDateUTC = dayjs().utc();
		return currentDateUTC.isAfter(dayjs.utc(license.ends_at));
	}

	public checkZeroState (comments) {
		const previousZeroState = this.zeroState;
		if (comments.length || !this.initialFeedbackLoaded) {
			this.zeroState = null;
		} else {
			if (this.ngxCommentListService.currentComments.length) {
				this.zeroState = FeedbackDisplayZeroState.NO_FEEDBACK_SHOWN;
			} else {
				const { activity } = this.$scope.mainController;
				const useLlmDefinitions = this.featureFlag.isAvailable('LLM_TASKS');

				if (this.$scope.mainController.license?.salesforce_license.ai_prompts_enabled &&
					activity.ai_prompts_enabled &&
					this.$scope.mainController.userGroup.hasInstructorRole(true) &&
					this.$scope.mainController.session.media &&
					!this.$scope.mainController.session.media.isYoutube() &&
					((useLlmDefinitions && (!activity.llm_definitions || activity.llm_definitions.length === 0)) ||
						(!useLlmDefinitions && (!activity.ai_prompts || activity.ai_prompts.length === 0))) &&
					!(activity.parent_child_sync && activity.is_parent_sync_enabled) &&
					!this.environmentVarsService.get(EnvironmentVarsService.VAR.DISABLE_ACTIVITY_EDITING)) {
					this.zeroState = FeedbackDisplayZeroState.ADD_AI_MARKERS_COMMENT;
				} else {
					this.zeroState = FeedbackDisplayZeroState.ADD_COMMENTS;
				}
			}
		}
		if (previousZeroState !== this.zeroState) {
			this.safeDigest();
		}
	}

	public toggleGeneratingAiFeedbackBanner (value: boolean) {
		this.showGeneratingAiFeedbackBanner = value;
		this.$scope.$applyAsync();
	}

	public toggleIsEdittingCommentState (value: boolean) {
		this.isReplyingOrUpdatingComment$.next(value);
	}

	private feedbackLoaded = () => {
		// Don't want to load rubrics until feedback is loaded
		// so we can accurately know if a user needs to create a new one
		// or use an existing one
		this.initialFeedbackLoaded = true;
		// TODO move to single variable or subject when ngx
		this.feedbackLoaded$.next(true);
	};

	private activityWatch = () => {
		if (this.canSeeCommentsOrRubrics()) {
			const activity = this.$scope.mainController.activity;

			if (this.feedbackSession.isRecording()) {
				if (activity.hasRecordingInstructions()) {
					this.ngxFeedbackSessionService.setActiveTab(FeedbackTabs.INFORMATION);
				}
			} else {
				this.ngxFeedbackSessionService.setActiveTab(FeedbackTabs.COMMENT);
			}
		} else {
			this.ngxFeedbackSessionService.setActiveTab(FeedbackTabs.INFORMATION);
		}
	};

	public canSeeCommentsOrRubrics = (): boolean => {
		const { activity } = this.$scope.mainController;
		const isRecordMode = this.feedbackSession.isRecording();
		const hasLiveSession = activity.live_session_enabled;

		if (isRecordMode && !hasLiveSession) {
			return false;
		}

		if (activity.isCommentsEnabled()) {
			return true;
		}

		if (activity.isPeerRubricEnabled()) {
			return true;
		}

		if (activity.isRubricEnabled()) {
			const group = this.selectedService.getGroup();
			const session = this.selectedService.getSession();
			const user = this.userService.currentUser;
			return group.hasReviewerRole(true) || session.isOwner(user);
		}

		return false;
	};

	private safeDigest () {
		// eslint-disable-next-line angular/no-private-call
		const phase = this.$scope.$root.$$phase || this.$scope.$$phase;
		if (!phase) {
			this.$scope.$digest();
		}
	}
}
